import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, createFilterOptions, Box, Typography, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { getInitialCompanies, searchCompanyList } from '../data/companies';

const filter = createFilterOptions();

const CompanyAutocomplete = React.forwardRef(({ onCompanySelect, darkMode = false, isBusinessDashboard = false, isLandingPage = false }, ref) => {
  const [companies, setCompanies] = useState(getInitialCompanies());
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [type, setType] = useState('existing');
  const [open, setOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [closeTimeout, setCloseTimeout] = useState(null);

  const handleInputChange = async (event, newInputValue, reason) => {
    if (type === 'startup') return;
    
    if (reason === 'clear' || reason === 'reset') {
      setInputValue('');
      setOpen(false);
      return;
    }
    
    setInputValue(newInputValue);
    
    if (newInputValue?.length >= 1) {
      const localResults = searchCompanyList(newInputValue);
      const transformedResults = localResults.map(company => ({
        ...company,
        _id: company.id
      }));
      setCompanies(transformedResults);
      setOpen(true);
    } else {
      setCompanies(getInitialCompanies());
      setOpen(false);
    }
  };

  const handleChange = (event, newValue) => {
    if (type !== 'existing') return;

    if (!newValue) {
      setValue(null);
      setInputValue('');
      setOpen(false);
      onCompanySelect(null);
      return;
    }

    if (newValue.isNew) {
      const newCompany = {
        id: `new-${Date.now()}`,
        name: newValue.inputValue,
        type: 'existing',
        isNew: true
      };
      setValue(null);
      onCompanySelect(newCompany);
    } else {
      const company = {
        id: newValue.id,
        name: newValue.name,
        type: 'existing'
      };
      setValue(null);
      onCompanySelect(company);
    }
    
    setInputValue('');
    setOpen(false);
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setType(newType);
    setOpen(false);
    
    if (newType === 'startup') {
      const startupCompany = {
        id: `startup-${Date.now()}`,
        name: 'New Startup INC',
        isNew: true,
        type: 'startup'
      };
      setValue(null);
      onCompanySelect(startupCompany);
      setInputValue('New Startup INC');
    } else {
      setValue(null);
      onCompanySelect(null);
      setInputValue('');
    }
  };

  const handleTouchStart = (event) => {
    event.preventDefault(); // Prevent double-tap zoom on mobile
  };

  const handleClose = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
    }
    
    const timeout = setTimeout(() => {
      setOpen(false);
    }, 5000);
    
    setCloseTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [closeTimeout]);

  // Add this method to refresh companies
  const refreshCompanies = () => {
    setCompanies(getInitialCompanies());
  };

  // Expose the refresh method via ref
  React.useImperativeHandle(ref, () => ({
    refreshCompanies
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
      <RadioGroup
        row
        value={type}
        onChange={handleTypeChange}
        sx={{
          mb: 2,
          '& .MuiFormControlLabel-label': {
            color: darkMode ? 'white' : 'inherit',
          }
        }}
      >
        <FormControlLabel 
          value="existing" 
          control={<Radio sx={{ color: darkMode ? 'white' : 'inherit' }} />} 
          label="Existing Company" 
        />
        <FormControlLabel 
          value="startup" 
          control={<Radio sx={{ color: darkMode ? 'white' : 'inherit' }} />} 
          label="New Startup Opportunity" 
        />
      </RadioGroup>

      {type === 'existing' && (
        <Autocomplete
          value={value}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          open={open}
          onClose={handleClose}
          readOnly={type === 'startup'}
          disablePortal={type === 'startup'}
          filterOptions={(options, params) => {
            const filtered = options.filter(option => {
              const searchTerm = params.inputValue.toLowerCase();
              return option.name.toLowerCase().includes(searchTerm);
            });
            
            if (!isBusinessDashboard && !filtered.length && params.inputValue) {
              filtered.push({
                inputValue: params.inputValue,
                name: `Add "${params.inputValue}"`,
                isNew: true
              });
              setOpen(true);
            }

            return filtered;
          }}
          options={type === 'startup' ? [] : companies}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.inputValue) return option.inputValue;
            return option.name;
          }}
          renderOption={(props, option) => (
            <li {...props} style={{ 
              color: darkMode ? '#fff' : '#000',
              padding: '10px 16px'
            }}>
              {option.isNew ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: '#3EAEFF',
                  gap: 1 
                }}>
                  <span>➕</span>
                  <span>Add "{option.inputValue}"</span>
                </Box>
              ) : (
                option.name
              )}
            </li>
          )}
          PaperComponent={({ children, ...props }) => (
            <Box
              component="div"
              {...props}
              sx={{
                backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                mt: 1,
                '& .MuiAutocomplete-listbox': {
                  padding: '8px',
                  '& li': {
                    borderRadius: '8px',
                    margin: '4px 0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(62,174,255,0.1)' : 'rgba(0,0,0,0.04)'
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: darkMode ? 'rgba(62,174,255,0.2)' : 'rgba(0,0,0,0.08)'
                    }
                  }
                }
              }}
            >
              {children}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={type === 'startup' ? '' : "Search existing companies"}
              variant="standard"
              disabled={type === 'startup'}
              InputProps={{
                ...params.InputProps,
                readOnly: type === 'startup',
                style: { 
                  backgroundColor: 'transparent'
                }
              }}
              sx={{
                width: {
                  xs: '100%',  // Full width on mobile
                  sm: '350px'  // 350px on tablets and up
                },
                '& .MuiInput-root': {
                  color: darkMode ? 'white !important' : 'inherit',
                  fontSize: darkMode ? '2rem' : 'inherit',
                  '&:before': { 
                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)' 
                  },
                  '&:after': { 
                    borderColor: darkMode ? '#3EAEFF' : '#3EAEFF' 
                  },
                  '&.Mui-disabled': {
                    color: `${darkMode ? 'white' : 'inherit'} !important`,
                    '-webkit-text-fill-color': `${darkMode ? 'white' : 'inherit'} !important`,
                    opacity: '1 !important',
                    cursor: type === 'startup' ? 'default' : 'not-allowed',
                    backgroundColor: 'transparent'
                  }
                },
                '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                  display: type === 'startup' ? 'none' : 'inline-flex',
                  color: darkMode ? 'white' : 'inherit'
                },
                '& input': {
                  color: `${darkMode ? 'white' : 'inherit'} !important`,
                  '-webkit-text-fill-color': `${darkMode ? 'white' : 'inherit'} !important`,
                  opacity: '1 !important',
                  cursor: type === 'startup' ? 'default' : 'text',
                  backgroundColor: 'transparent'
                },
                '& .MuiInputBase-root': {
                  backgroundColor: 'transparent'
                }
              }}
            />
          )}
          disableClearable={type === 'startup'}
          noOptionsText={
            <Typography sx={{ 
              color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              textAlign: 'center',
              py: 1
            }}>
              No companies found
            </Typography>
          }
          PopperProps={{
            style: {
              width: 'auto',
              maxWidth: '100vw'
            },
            placement: "bottom-start"
          }}
          onTouchStart={handleTouchStart}
          disableTouchRipple={false}
        />
      )}

      {(isLandingPage || isBusinessDashboard) && (
        <Typography 
          variant="caption" 
          sx={{ 
            color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
            textAlign: 'center',
            mt: 1
          }}
        >
          {isBusinessDashboard ? (
            type === 'startup' 
              ? "View startup suggestions from users"
              : "View feedback and suggestions for your company"
          ) : (
            type === 'startup' 
              ? "Add to a list of startups that people wish existed!"
              : "Give suggestions or complain to an existing company or add a new one"
          )}
        </Typography>
      )}
    </Box>
  );
});

export default CompanyAutocomplete; 